import { Table, Pagination, HeadItemType } from 'components'
import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { getOrderTracking } from 'services/salesOrder'

import * as S from '../styles'
import { Skeleton } from '@mui/material'
import { toast } from 'react-toastify'
import { OrderTracking } from 'services/salesOrder/types'
import { formatDate } from 'commons/utils/date'

const ModalTrackingOrder = () => {
  const { orderNumber } = useParams()
  const [loading, setLoading] = useState(true)
  const [trackings, setTrackings] = useState<OrderTracking[]>([])

  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalPages, setTotalPages] = useState<number>(0)
  const [totalItems, setTotalItems] = useState<number>(0)
  const PAGE_SIZE = 10

  const isMounted = useRef(true)

  const headValues: HeadItemType[] = [
    { label: 'Data', columnName: 'Data' },
    { label: 'Hora', columnName: 'Hora' },
    { label: 'Usuário', columnName: 'Usuario' },
    { label: 'Descrição', columnName: 'Descricao' },
    { label: 'Complemento', columnName: 'Complemento' }
  ]

  const renderSkeleton = useCallback(() => {
    const skeleton = []
    for (let i = 0; i < 3; i++) {
      skeleton.push({
        id: `tracking-loading-${i}`,
        content: [
          <Skeleton key={`tracking-skeleton-1-${i}`} width={130} height={30} />,
          <Skeleton key={`tracking-skeleton-2-${i}`} width={80} height={30} />,
          <Skeleton key={`tracking-skeleton-3-${i}`} width={100} height={30} />,
          <Skeleton key={`tracking-skeleton-4-${i}`} width={180} height={30} />,
          <Skeleton key={`tracking-skeleton-5-${i}`} width={300} height={30} />
        ]
      })
    }
    return skeleton
  }, [])

  const renderBody = useCallback(() => {
    if (loading) return renderSkeleton()
    if (!trackings.length) return []

    return trackings.map((tracking, index) => {
      return {
        id: `tracking-item-${index}`,
        content: [
          tracking.dt_cadt
            ? formatDate(tracking.dt_cadt, 'DD/MM/YYYY', 'DD/MM/YYYY HH:mm:ss')
            : '--',
          tracking.dt_cadt
            ? formatDate(tracking.dt_cadt, 'HH[h]mm', 'DD/MM/YYYY HH:mm:ss')
            : '--',
          tracking.cd_usua_cadt ?? '--',
          tracking.ds_ocor_trac ?? '--',
          tracking.ds_cmpt_ocor_trac ?? '--'
        ]
      }
    })
  }, [loading, trackings, renderSkeleton])

  useEffect(() => {
    isMounted.current = true

    const getTracking = async () => {
      setLoading(true)
      try {
        const response = await getOrderTracking(
          { paginas: currentPage, linhas: PAGE_SIZE },
          `${orderNumber}`
        )
        if (isMounted.current) {
          const rowsCount = response[0]?.rowscount ?? 0
          setTrackings(response)
          setTotalItems(rowsCount)
          setTotalPages(Math.ceil(rowsCount / PAGE_SIZE))
        }
      } catch (error) {
        if (isMounted) toast.error('Erro ao carregar rastreamento')
      } finally {
        if (isMounted.current) setLoading(false)
      }
    }

    if (orderNumber) getTracking()

    return () => {
      isMounted.current = false
    }
  }, [currentPage, orderNumber])

  return (
    <>
      <S.TableWrapper>
        <Table
          headData={headValues}
          bodyData={renderBody()}
          notScrollable
          rowHeight={40}
        />
      </S.TableWrapper>

      {trackings.length > 0 && (
        <Pagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          numberOfPages={totalPages}
          numberOfItems={totalItems}
          itemsPerPage={PAGE_SIZE}
          disabled={loading}
        />
      )}
    </>
  )
}

export default ModalTrackingOrder
